import type {
  VoicebotState,
  VoicebotAPI,
  DynamicValidations,
  APIVoicebotPromptVariablesData,
} from 'src/types/voicebot';
import {
  parseNodeSchedule,
  normalizeNodeSchedule,
} from 'src/utils/node-schedule';


export const ConfigPageType = Object.freeze({
  SELECT_VOICEBOT: 'SELECT_VOICEBOT',
  VOICEBOT_CONFIG: 'VOICEBOT_CONFIG',
});

/**
 * Converts comma separated prompt variables to object
 * @param {*} vars eg: 'ae_job/title,ae_job/state,ae_candidate/full_name,ae_candidate/first_name,ae_job/city'
 * @returns object { 'ae_job/title': null, 'ae_job/state': null, ...}
 */
export const transformPromptVariables = (
  vars: string | null,
): VoicebotState['prompt_variables'] => {
  const result: Record<string, any> = {};
  if (vars === null || vars === '') {
    return result;
  }

  // Split the input string by commas to get an array of keys
  const keys = vars.split(',');

  keys.forEach((key) => {
    result[key] = null;
  });

  return result;
};

// Changes field names of API response
export const transformStaticVariables = (
  vars: Array<{
    key: string;
    attribute: string;
    display_name: string;
  }>,
): APIVoicebotPromptVariablesData['static_variables'] => {
  return vars.map((variable) => ({
    name: variable.key,
    value: variable.display_name,
  }));
};

export const parseVoicebot = (data: VoicebotAPI): VoicebotState => {
  const voicebotParams = data.task.params;

  if (!voicebotParams) {
    throw new Error('Voiceflow params not found');
  }

  const {
    retell_bot_id,
    retell_agent_id,
    retell_bot_name,
    retell_bot_description,
    retell_llm_id,
    consent_type,
    max_concurrency_limit,
    service_provider,
    entity_type,
    from_number,
    to_number,
    recipient_name,
    prompt_variables,
  } = voicebotParams;
  const nodeSchedule = parseNodeSchedule(data);

  return {
    retell_bot_id,
    retell_agent_id,
    retell_bot_name,
    retell_bot_description,
    retell_llm_id,
    consent_type,
    max_concurrency_limit,
    service_provider,
    entity_type,
    from_number,
    to_number,
    recipient_name,
    prompt_variables,
    ...nodeSchedule,
  };
};

export const normalizeVoicebot = (
  voicebotState: VoicebotState,
): VoicebotAPI => {
  const {
    retell_bot_id,
    retell_agent_id,
    retell_bot_name,
    retell_bot_description,
    retell_llm_id,
    consent_type,
    max_concurrency_limit,
    service_provider,
    entity_type,
    from_number,
    to_number,
    recipient_name,
    prompt_variables,
  } = voicebotState;
  const nodeSchedule = normalizeNodeSchedule(voicebotState);

  return {
    task: {
      params: {
        retell_bot_id,
        retell_agent_id,
        retell_bot_name,
        retell_bot_description,
        retell_llm_id,
        consent_type,
        max_concurrency_limit,
        service_provider,
        entity_type,
        from_number,
        to_number,
        recipient_name,
        prompt_variables,
      },
    },
    ...nodeSchedule,
  };
};

export const getErrors = (validations: DynamicValidations): Array<string> => {
  const errors: Array<string> = [];
  const {
    callToNumberEmpty,
    callToNumberInvalid,
    recipientNameEmpty,
    ...dynamicPromptVars
  } = validations || {};
  const anyDynamicVarEmpty = Object.keys(dynamicPromptVars)
    .filter((promptVar) => promptVar.endsWith('_empty'))
    .some((promptVar) => Boolean(dynamicPromptVars[promptVar]));
  const anyDynamicVarInvalid = Object.keys(dynamicPromptVars)
    .filter((promptVar) => promptVar.endsWith('_invalid'))
    .some((promptVar) => Boolean(dynamicPromptVars[promptVar]));

  if (anyDynamicVarEmpty || callToNumberEmpty || recipientNameEmpty) {
    errors.push('Please fill the required fields.');
  }
  if (anyDynamicVarInvalid) {
    errors.push('Please enter valid variables.');
  }
  if (callToNumberInvalid) {
    errors.push('Please enter correct call to number.');
  }
  return errors;
};

/**
 * reason will be of this type 'dial_no_answer'
 * returns 'dial no answer'
 */
export const formatFailureReason = (reason: unknown): string => {
  if (!reason || typeof reason !== 'string') {
    return '';
  }
  return reason.split('_').join(' ');
};

/**
 * Populates target object with values from source object where keys match
 * @param {Object} to - Target object to be populated
 * @param {Object} from - Source object containing values to populate from
 * @returns {Object} Updated target object
 */
export const populatePromptVariables = (
  to: {
    [key: string]: string | null | undefined;
  },
  from: {
    [key: string]: string | null | undefined;
  },
): {
  [key: string]: string | null | undefined;
} => {
  const result = {...to} as const;
  Object.keys(result).forEach((key) => {
    if (key in from) {
      // @ts-expect-error - TS2542 - Index signature in type '{ readonly [key: string]: string | null | undefined; }' only permits reading.
      result[key] = from[key];
    }
  });

  return result;
};
